/* eslint-disable react/no-unstable-nested-components */
import {
  Alert,
  Button,
  Divider,
  Input,
  InputNumber,
  Select,
  Switch,
} from 'antd';
import DynamicForm, {
  FormButton,
  FormEntry,
  FormValues,
} from '@ynomia/dynamic-form';
import { Field, FormikProps } from 'formik';
import React, { memo } from 'react';
import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import { DateTime, SegmentedPicklist } from '..';
import styles from './styles.module.less';
import {
  DELIMITER,
  entryTransformations,
} from './transformations';
import { displayPercentage, parsePercentageToDecimal } from '../../../utils';

export interface Props {
  fields: Array<FormEntry>;
  defaultValues?: FormValues;
  submitButtonText?: string;
  hideSubmitButton?: boolean;
  hideCancelButton?: boolean;
  disableSubmitButton?: boolean;
  isDisabled?: boolean;
  submitButtonLoading?: boolean;
  filtrexProps?: { [key: string]: any };
  onCancel?: () => void
  onSubmit?: (formValues: FormValues) => void | Promise<void>;
  onValueChange?: (
    fieldName: string,
    value: any,
    output: { [id: string]: any },
    formikProps: FormikProps<FormValues>,
  ) => void | Promise<void>;
  onEvent?: (key: string, event: string, value: string) => void;
  dynamicFormRef?: (ref: DynamicForm | null) => void;
  extraFiltrexFunctions?: { [x: string]: (str?: string) => any };
  scrollable?: boolean;
  fill?: boolean;
}

/**
 * A styled implementation of Ynomia's "Dynamic Form" library, designed to be presented and
 * reused across numerous modal screens throughout the app.
 * @param {Props} props
 * @param {Array<FormEntry>} props.fields
 * @param {FormValues?} props.defaultValues
 * @param {string?} props.submitButtonText: Changes the text of the bottom fixed action button.
 * Defaults to "Submit".
 * @param {boolean?} props.hideSubmitButton: Do not show the option to submit the form.
 * @param {boolean?} props.hideCancelButton: Do not show the option to cancel the form.
 * @param {boolean?} props.disableSubmitButton: Disable just the submit button.
 * @param {boolean?} props.isDisabled: If `true`, the entire form will appear disabled,
 * including buttons.
 * @param {boolean?} props.submitButtonLoading: If `true`, the submit button will
 * show a loading indicator.
 * @param {any} props.filtrexProps: Provide additional properties that can be used in filtrex
 * conditionals for this form instance. See the Dynamic Form library docs for more information.
 * @param {any} props.onCancel
 * @param {any} props.onSubmit
 * @param {Function?} props.onValueChange: Callback function for each time a form field changes
 * as a result of a user's action (eg typing, new selection etc).
 * @param {boolean?} props.scrollable: Caps the form's height and scrolls it, for use in a modal.
 * Pass `false` when the form sits inside a container that already scrolls. Defaults to `true`.
 * @param {boolean?} props.fill: Takes the height left in a flex column parent instead of a fixed
 * cap, scrolling the fields and keeping the buttons in view. For modals with more around the
 * form than a title and buttons.
 */
const ModalForm = ({
  fields,
  defaultValues,
  onSubmit,
  submitButtonText,
  onCancel,
  hideSubmitButton,
  hideCancelButton,
  submitButtonLoading = false,
  isDisabled = false,
  onValueChange,
  onEvent,
  filtrexProps,
  dynamicFormRef,
  extraFiltrexFunctions,
  disableSubmitButton = false,
  scrollable = true,
  fill = false,
}: Props) => {
  const handleRef = (ref: DynamicForm | null) => {
    dynamicFormRef?.(ref);
  };

  const formButtons: Array<FormButton> = [];

  if (!hideSubmitButton) {
    formButtons.push({
      type: 'SUBMIT',
      style: 'default',
      text: submitButtonText,
      id: 'submit',
    });
  }

  if (!hideCancelButton) {
    formButtons.push({
      type: 'CANCEL',
      style: 'default',
      text: 'Cancel',
      id: 'cancel',
    });
  }

  const generateButtons = (buttons: Array<FormButton>, formikProps: FormikProps<FormValues>) => (
    <div className={styles.buttonContainer}>
      {
        buttons.map((button) => {
          if (button.type === 'SUBMIT') {
            return (
              <Button
                key={button.id}
                style={{ marginLeft: 8 }}
                onClick={() => formikProps.handleSubmit()}
                type="primary"
                loading={submitButtonLoading}
                disabled={
                  (formikProps.submitCount > 0 && !formikProps.isValid)
                  || isDisabled
                  || disableSubmitButton
                }
              >
                {`${button.text || 'Submit'}`}
              </Button>
            );
          }

          if (button.type === 'CANCEL') {
            return (
              <Button
                key={button.id}
                onClick={onCancel}
                disabled={submitButtonLoading}
                type="default"
              >
                {`${button.text || 'Cancel'}`}
              </Button>
            );
          }
          return <div key={button.id}>{button.customButton}</div>;
        })
      }
    </div>
  );

  const handleSubmit = (formValues: FormValues) => {
    const processedFormValues = Object.entries(formValues || {})
      .filter(([, value]) => value !== undefined)
      .reduce((all, [fieldId, value]) => ({
        ...all,
        [fieldId]: value,
      }), {});

    if (onSubmit) {
      onSubmit(processedFormValues);
    }
  };

  const renderFieldHeader = (
    settings: FormEntry,
    entryShouldBeValidated: boolean,
    form: FormikFieldData['form'],
    field: FormikFieldData['field'],
    formikProps: FormikProps<FormValues>,
  ) => {
    const hasBeenTouched = form.touched[field.name];
    const hasBeenSubmitted = formikProps.submitCount > 0;
    const shouldShowError = entryShouldBeValidated
      && (hasBeenTouched || hasBeenSubmitted)
      && !submitButtonLoading;
    return (
      <div key={field.name} className={styles.formTitle}>
        <span>{settings.label}</span>
        <span className={styles.asteriskText}>{settings.isRequired === true && '*'}</span>
        {shouldShowError && <span className={styles.requiredText}>{form.errors[field.name]}</span>}
      </div>
    );
  };

  type EntryComponentProps = {
    settings: FormEntry;
    formikProps: FormikProps<FormValues>;
    disabled: boolean;
    entryShouldBeValidated: boolean;
  };

  type FormikFieldData = {
    field: {
      name: string;
      value: any;
      onChange: () => void | Promise<void>;
      onBlur: () => void | Promise<void>;
    };
    form: {
      touched: { [field: string]: boolean };
      errors: { [field: string]: string };
    };
  };

  const formikValueChange = (
    formikProps: FormikProps<FormValues>,
    fieldName: string,
    value: string,
  ) => {
    formikProps.setFieldTouched(fieldName, true);
    formikProps.handleChange(fieldName)(value);

    const transformedFormikProps: { [id: string]: any } = {};
    const newValues = { ...formikProps.values, [fieldName]: value };

    fields
      .filter(field => newValues[field.id])
      .forEach((field) => {
        // entryTransformations is keyed by entryComponent ids
        // @ts-ignore
        const transform = entryTransformations[field.entryComponent]?.out;
        if (transform) {
          transformedFormikProps[field.id] = transform(newValues[field.id]);
        } else {
          transformedFormikProps[field.id] = newValues[field.id];
        }
      });

    onValueChange?.(
      fieldName,
      transformedFormikProps[fieldName],
      transformedFormikProps,
      formikProps,
    );
  };

  const disableForm = submitButtonLoading || isDisabled;

  const entryComponents = {
    divider: ({ settings }) => <Divider key={settings.id} />,
    /**
     * A section header that expands and collapses. Its value is '1' while expanded: entries in
     * the section use `show: '<id> of values'`, with `allowHiddenSubmit` so collapsing it
     * doesn't drop their values.
     */
    collapse: ({ settings, formikProps }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field }: FormikFieldData) => (
          // Wrapped so it starts its own line rather than sitting beside an inline entry.
          <div className={styles.collapseHeader}>
            <Button
              type="link"
              icon={<RightOutlined rotate={field.value ? 90 : 0} />}
              onClick={() => formikProps.setFieldValue(field.name, field.value ? '' : '1')}
            >
              {settings.label}
            </Button>
          </div>
        )}
      </Field>
    ),
    text: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => (
          <>
            {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
            <Input
              id={`${settings.id}-input-id`}
              key={`${settings.id}-input-key`}
              placeholder={settings.properties?.placeholder}
              value={field.value}
              onChange={(e) => {
                formikValueChange(formikProps, field.name, e.target.value);
              }}
              disabled={disabled || disableForm}
              onFocus={() => onEvent?.(settings.id, 'onFocus', field.value)}
              autoFocus={settings.properties?.autoFocus}
              variant={settings.properties?.variant as any}
              style={settings.properties?.style}
            />
          </>
        )}
      </Field>
    ),
    textarea: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => (
          <>
            {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
            <TextArea
              rows={settings.properties?.minHeight || 4}
              autoSize={settings.properties?.autoSize}
              style={{ color: 'black' }}
              placeholder={settings.properties?.placeholder}
              value={field.value}
              onChange={e => formikValueChange(formikProps, field.name, e.target.value)}
              disabled={disabled || disableForm}
              onFocus={() => onEvent?.(settings.id, 'onFocus', field.value)}
            />
          </>
        )}
      </Field>
    ),
    number: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => {
          const { properties } = settings;
          const { min, max, placeholder } = properties || {};
          const numberIsPercentage = settings?.properties?.numberFormat === 'percentage';

          return (
            <div>
              {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
              <InputNumber
                min={min}
                max={max}
                step={numberIsPercentage ? 0.01 : undefined}
                value={field.value}
                onChange={value => formikValueChange(
                  formikProps,
                  field.name,
                  value == null
                    ? ''
                    : String(value),
                )}
                formatter={numberIsPercentage
                  ? value => displayPercentage(value)
                  : undefined}
                parser={
                  numberIsPercentage
                    ? display => parsePercentageToDecimal(display!)
                    : undefined
                }
                placeholder={placeholder}
                disabled={disabled || disableForm}
                onFocus={() => onEvent?.(settings.id, 'onFocus', field.value)}
              />
            </div>
          );
        }}
      </Field>
    ),
    picklist: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => {
          const { properties, isRequired } = settings;
          const {
            tags,
            multi,
            maxCount,
            options,
          } = properties || {};

          const getPicklistMode = () => {
            if (tags) return 'tags';
            if (multi) return 'multiple';
            return undefined;
          };
          return (
            <>
              {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
              <Select
                allowClear={!isRequired}
                mode={getPicklistMode()}
                value={field.value ? field.value.split?.(DELIMITER) : undefined}
                disabled={disabled || disableForm}
                placeholder={properties?.placeholder || 'Please select'}
                options={options}
                onChange={(value) => {
                  if (Array.isArray(value)) {
                    formikValueChange(formikProps, field.name, value.join(DELIMITER));
                  } else {
                    formikValueChange(formikProps, field.name, value || '');
                  }
                }}
                getPopupContainer={triggerNode => triggerNode.parentElement}
                style={{ width: '100%' }}
                maxCount={maxCount}
                dropdownStyle={{ position: 'fixed' }}
                onFocus={() => onEvent?.(settings.id, 'onFocus', field.value)}
              />
            </>
          );
        }}
      </Field>
    ),
    segmented_picklist: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => (
          <>
            {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
            <SegmentedPicklist
              columns={settings.properties?.columns}
              value={field.value ? JSON.parse(field.value) : undefined}
              onValueChange={(value) => {
                formikValueChange(formikProps, field.name, value ? JSON.stringify(value) : '');
              }}
              disabled={disabled || disableForm}
              resetValuesOnChange={settings.properties?.resetValuesOnChange}
            />
          </>
        )}
      </Field>
    ),
    checkbox: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => (
          <>
            {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
            <Switch
              onChange={value => formikValueChange(formikProps, field.name, value ? '1' : '')}
              value={field.value}
              disabled={disabled || disableForm}
            />
          </>
        )}
      </Field>
    ),
    date: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => {
      const {
        format,
        restriction,
        allowClear,
      } = settings.properties || {};
      return (
        <Field name={settings.id} key={settings.id}>
          {({ field, form }: FormikFieldData) => (
            <>
              {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
              <DateTime
                onChange={value => formikValueChange(formikProps, field.name, value ?? '')}
                disabled={disabled || disableForm}
                value={field.value}
                format={format}
                restriction={restriction}
                allowClear={allowClear}
                mode="date"
              />
            </>
          )}
        </Field>
      );
    },
    time: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => {
          const {
            timezone,
            errorMessage,
            restriction,
            allowClear,
            format,
          } = settings.properties || {};

          return (
            <>
              {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
              <DateTime
                onChange={value => formikValueChange(formikProps, field.name, value ?? '')}
                disabled={disabled || disableForm}
                value={field.value}
                format={format}
                allowClear={allowClear}
                restriction={restriction}
                mode="time"
              />
              {timezone && (
              <div
                className={`
                  ${styles.timezoneContainer}
                  ${timezone.disabled && styles.timezoneInfoContainerDisabled}
                  ${errorMessage && styles.timezoneInfoContainerError}
                `}
              >
                <InfoCircleOutlined className={styles.timezoneInfoIcon} />
                <span className={styles.timezoneInfoText}>{timezone}</span>
              </div>
              )}
            </>
          );
        }}
      </Field>
    ),
    datetime: ({
      settings,
      formikProps,
      disabled,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => {
          const {
            timezone,
            errorMessage,
            restriction,
            allowClear,
            split,
            format,
          } = settings.properties || {};
          return (
            <>
              {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
              <DateTime
                onChange={value => formikValueChange(formikProps, field.name, value ?? '')}
                disabled={disabled || disableForm}
                value={field.value}
                format={format}
                restriction={restriction}
                mode={split ? 'dateTimeSplit' : 'datetime'}
                allowClear={allowClear}
                style={timezone ? {
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                } : {}}
              />
              {timezone && (
              <div
                className={`
                  ${styles.timezoneContainer}
                  ${timezone.disabled && styles.timezoneInfoContainerDisabled}
                  ${errorMessage && styles.timezoneInfoContainerError}
                `}
              >
                <InfoCircleOutlined className={styles.timezoneInfoIcon} />
                <span className={styles.timezoneInfoText}>{timezone}</span>
              </div>
              )}
            </>
          );
        }}
      </Field>
    ),
    photo: ({
      settings,
      formikProps,
      entryShouldBeValidated,
    }: EntryComponentProps) => (
      <Field name={settings.id} key={settings.id}>
        {({ field, form }: FormikFieldData) => (
          <>
            {renderFieldHeader(settings, entryShouldBeValidated, form, field, formikProps)}
            <Alert
              message={`
                This feature isn’t supported on the web version.
                Use the Ynomia Mobile App to take photos.
              `}
              type="warning"
              showIcon
            />
          </>
        )}
      </Field>
    ),
  };

  const formContentContainer = (formContent: any) => (
    <div
      className={scrollable ? styles.formContentContainer : undefined}
      style={fill ? {
        flex: 1, minHeight: 0, maxHeight: 'none',
      } : undefined}
    >
      {formContent}
    </div>
  );

  return (
    <div
      // Sometimes parent elements can block certain key press behaviours.
      // "stopPropagation" stops this behaviour.
      onKeyDown={e => e.stopPropagation()}
      role="none"
      style={fill ? {
        display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0,
      } : undefined}
    >
      <DynamicForm
        entries={fields as any}
        key={JSON.stringify(fields)}
        entryComponents={entryComponents}
        entryTransformers={entryTransformations}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        buttons={formButtons}
        generateButtons={generateButtons}
        contentContainer={formContentContainer}
        validateOnMount
        filtrexProps={filtrexProps}
        ref={ref => handleRef(ref)}
        extraFiltrexFunctions={extraFiltrexFunctions}
      />
    </div>
  );
};

export default memo(ModalForm);
